import he from 'he';
import Big from 'big.js';
import { Map } from 'immutable';

import { newDetails, newInventory, newShipping, newVariations } from '../new/product';
import { shapePersonalizationForApp } from './shapeForApp';
import { convertVariations } from '../variations/convert';
import { setProductErrors } from './errors';
import { tagsFromArray } from '../tags';
import { getTagError } from '../validations/tags';
import { getSize } from '../iterable/getSize';

import { ATTRIBUTE_DEFAULTS, OVERSET, VALUE } from '../../constants/product';
import { MAX_NUMBER_OF_TAGS } from '../../constants/validations';
import { EMPTY, SEPARATOR } from '../../constants';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { COMBINATIONS } from '../../constants/variations';
import { FORM } from '../../constants/forms';
import ATTRIBUTE from '../../constants/attributes';

export function convertWeightToLbAndOz(weight) {
  try {
    const number = new Big(weight);
    const lb = number.div(16).round(0, 0);
    const oz = lb.abs().gt(0) ? number.minus(lb.times(16)) : number;
    return {
      lb: lb.toString(),
      oz: oz.round(4).toString(),
    };
  } catch (error) {
    return { lb: '', oz: '' };
  }
}

export function convertLbAndOzToWeight(pound, ounce) {
  let lb;
  let oz;

  try {
    lb = new Big(pound);
  } catch (error) {
    lb = undefined;
  }

  try {
    oz = new Big(ounce);
  } catch (error) {
    oz = undefined;
  }

  if (lb && oz) {
    return lb.times(16).plus(oz).round(4).toString();
  } else if (lb && !oz) {
    return lb.times(16).round(4).toString();
  } else if (!lb && oz) {
    return oz.round(4).toString();
  } else {
    return '';
  }
}

export function convertDescriptionToBodyHTML(description) {
  if (!description) return description;

  function mapParagraphs(paragraph) {
    return paragraph.trim()
      ? `<p>${he.encode(paragraph, { strict: true, useNamedReferences: true })}</p>`
      : '<br/>';
  }

  return description.split(/\r?\n/).map(mapParagraphs).join('');
}

export function convertBodyHTMLToDescription(bodyHTML) {
  if (!bodyHTML || typeof bodyHTML !== 'string') return '';

  const element = document.createElement('div');
  element.innerHTML = bodyHTML
    .replace(/(<\/(p|li|td|th|h\d)>)+/g, '<br><br>')
    .replace(/<br[^>]*>/gi, '\n')
    .replace(/(^\n+)|<[^>]*>|<\/[^>]*>|(\n+$)/gi, '');

  // decode HTML entities
  return he.decode(element.innerText || element.textContent || '');
}

export function convertBodyHTMLToMetaDescription(bodyHTML) {
  return convertBodyHTMLToDescription(bodyHTML).replace(/\s+/g, SEPARATOR.SPACE).trim();
}

function filterTags(channel) {
  return function filter(tag) {
    return !getTagError({ channel, tag });
  };
}

export function convert({ from, to, type, value, ...rest }) {
  switch (type) {
    case ATTRIBUTE.DESCRIPTION: {
      switch (from) {
        case ETSY: {
          switch (to) {
            case SHOPIFY: {
              return convertDescriptionToBodyHTML(value);
            }

            default: {
              return value;
            }
          }
        }

        case SHOPIFY: {
          switch (to) {
            case ETSY: {
              return convertBodyHTMLToDescription(value);
            }

            default: {
              return value;
            }
          }
        }

        default: {
          return value;
        }
      }
    }

    case ATTRIBUTE.TAGS: {
      return value.filter(filterTags(to)).slice(0, MAX_NUMBER_OF_TAGS[to]);
    }

    case ATTRIBUTE.VARIATIONS: {
      return convertVariations({ from, to, variations: value, ...rest });
    }

    default: {
      return value;
    }
  }
}

export function convertProduct({ from, to, product: source, validate = true }) {
  let product = source.set('channel', to);

  function reduceCombinationsForPhysical(physical, combination) {
    return physical && combination.get(ATTRIBUTE.PHYSICAL);
  }

  switch (from) {
    case ETSY: {
      switch (to) {
        case ETSY: {
          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE])) {
            product = product
              .setIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE], null)
              .setIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_TYPE, VALUE], null);
          }

          if (getSize(product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE]))) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE],
              EMPTY.LIST
            );
          }

          if (product.hasIn([FORM.VARIATIONS, 'currentProfile'])) {
            product = product
              .deleteIn([FORM.VARIATIONS, 'operations'])
              .deleteIn([FORM.VARIATIONS, 'oldVariations'])
              .deleteIn([FORM.VARIATIONS, 'currentProfile']);
          }

          if (product.hasIn([FORM.TAGS, 'currentProfile'])) {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS],
                [ATTRIBUTE.NEW_TAGS]: tagsFromArray(product.getIn([FORM.TAGS, 'currentProfile', ATTRIBUTE.TAGS])),
              })
            );
          }

          if (product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE])) {
            product = product.setIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE], null);
          }

          if (product.getIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE])) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], null);
          }

          break;
        }

        case SHOPIFY: {
          const inventory = newInventory({ channel: to })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          product = product
            .delete(FORM.PERSONALIZATION)
            .delete(FORM.SHIPPING)
            .delete(FORM.FILES)
            .set(FORM.DESCRIPTION,
              Map({
                [VALUE]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.DESCRIPTION,
                  value: product.getIn([FORM.DESCRIPTION, VALUE]),
                }),
              }),
            )
            .set(FORM.DETAILS, newDetails({ channel: to }))
            .set(FORM.INVENTORY, inventory)
            .set(FORM.VARIATIONS,
              convert({
                from,
                inventory,
                to,
                type: ATTRIBUTE.VARIATIONS,
                value: product.get(FORM.VARIATIONS),
              })
            );

          if (product.hasIn([FORM.TAGS, 'currentProfile'])) {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS],
                [ATTRIBUTE.NEW_TAGS]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: tagsFromArray(product.getIn([FORM.TAGS, 'currentProfile', ATTRIBUTE.TAGS])),
                }),
              })
            );
          } else {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: product.getIn([FORM.TAGS, ATTRIBUTE.TAGS], ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS]),
                }),
                [ATTRIBUTE.NEW_TAGS]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: product.getIn([FORM.TAGS, ATTRIBUTE.NEW_TAGS], ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS]),
                }),
              })
            );
          }

          product = product.set(FORM.SEO,
            Map({
              [ATTRIBUTE.PAGE_TITLE]: Map({
                [VALUE]: product.getIn([FORM.TITLE, VALUE]),
                [OVERSET]: true,
              }),
              [ATTRIBUTE.META_DESCRIPTION]: Map({
                [OVERSET]: true,
                [VALUE]: convertBodyHTMLToMetaDescription(product.getIn([FORM.DESCRIPTION, VALUE])),
              }),
            })
          );

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case SHOPIFY: {
      switch (to) {
        case ETSY: {
          const inventory = newInventory({ channel: to })
            .setIn([ATTRIBUTE.PRICE, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]))
            .setIn([ATTRIBUTE.QUANTITY, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]))
            .setIn([ATTRIBUTE.SKU, VALUE], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]));

          product = product
            .delete(FORM.SEO)
            .set(FORM.DESCRIPTION,
              Map({
                [VALUE]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.DESCRIPTION,
                  value: product.getIn([FORM.DESCRIPTION, VALUE]),
                }),
              }),
            )
            .set(FORM.DETAILS, newDetails({ channel: to }))
            .set(FORM.INVENTORY, inventory);

          if (product.hasIn([FORM.TAGS, 'currentProfile'])) {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS],
                [ATTRIBUTE.NEW_TAGS]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: tagsFromArray(product.getIn([FORM.TAGS, 'currentProfile', ATTRIBUTE.TAGS])),
                }),
              })
            );
          } else {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: product.getIn([FORM.TAGS, ATTRIBUTE.TAGS], ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS]),
                }),
                [ATTRIBUTE.NEW_TAGS]: convert({
                  from,
                  to,
                  type: ATTRIBUTE.TAGS,
                  value: product.getIn([FORM.TAGS, ATTRIBUTE.NEW_TAGS], ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS]),
                }),
              })
            );
          }

          const physical = getSize(source.getIn([FORM.VARIATIONS, COMBINATIONS]))
            ? source
              .getIn([FORM.VARIATIONS, COMBINATIONS])
              .reduce(reduceCombinationsForPhysical, true)
            : source.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]);

          if (physical) {
            product = product
              .setIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE], false)
              .set(FORM.SHIPPING, newShipping({ channel: to }))
              .set(FORM.VARIATIONS,
                convert({
                  from,
                  inventory,
                  to,
                  type: ATTRIBUTE.VARIATIONS,
                  value: product.get(FORM.VARIATIONS),
                })
              );
          } else {
            product = product
              .setIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE], true)
              .set(FORM.FILES, Map({ [VALUE]: EMPTY.LIST }))
              .set(FORM.VARIATIONS, newVariations({ channel: to }));
          }

          product = product.set(FORM.PERSONALIZATION, shapePersonalizationForApp({}));

          break;
        }

        case SHOPIFY: {
          if (getSize(product.getIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE]))) {
            product = product.setIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE],
              ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.COLLECTIONS]
            );
          }

          if (product.hasIn([FORM.VARIATIONS, 'currentProfile'])) {
            product = product
              .deleteIn([FORM.VARIATIONS, 'operations'])
              .deleteIn([FORM.VARIATIONS, 'oldVariations'])
              .deleteIn([FORM.VARIATIONS, 'currentProfile']);
          }

          if (product.hasIn([FORM.TAGS, 'currentProfile'])) {
            product = product.set(FORM.TAGS,
              Map({
                [VALUE]: ATTRIBUTE_DEFAULTS[to][ATTRIBUTE.TAGS],
                [ATTRIBUTE.NEW_TAGS]: tagsFromArray(product.getIn([FORM.TAGS, 'currentProfile', ATTRIBUTE.TAGS])),
              })
            );
          }

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return validate
    ? setProductErrors({ product })
    : product;
}
